import type {
    ObjectProposalGenerationType,
    ProposalExtraItemType,
} from '@econtractor/types';
import vconst from 'utils/helpers/structs/vconst';
import { buildDemand } from '../../Proposal2Warehouse/model/buildDemand';
import { isProposalExtraAsOtherExpense } from '../../Proposal2Warehouse/model/addProposalExtraAsOtherExpend';
import { isOtherTeamIncome } from '../../model/isOtherTeamIncome';
import type { ProposalShortageItem, ProposalShortageResult } from './types';

const EPS = 1e-6;

const asRecord = (value: unknown): Record<string, unknown> | null =>
    value && typeof value === 'object' ? (value as Record<string, unknown>) : null;

const pickNumber = (row: Record<string, unknown>, ...keys: string[]): number => {
    for (const key of keys) {
        const raw = row[key];
        if (raw == null || raw === '') continue;
        const n = Number(raw);
        if (Number.isFinite(n)) return n;
    }
    return 0;
};

const pickText = (row: Record<string, unknown>, ...keys: string[]): string => {
    for (const key of keys) {
        const value = String(row[key] ?? '').trim();
        if (value) return value;
    }
    return '';
};

const normalizeName = (name: string) => name.trim().toLowerCase().replace(/\s+/g, ' ');

const toShortageItem = (params: {
    key: string;
    name: string;
    need: number;
    have: number;
    article?: string;
    dimensionName?: string;
    productId?: number;
    serviceId?: number;
    price?: number;
    priceRate?: number;
    priceSale?: number;
    priceSaleRate?: number;
    asOtherExpense?: boolean;
}): ProposalShortageItem | null => {
    const need = Number(params.need) || 0;
    const have = Math.max(0, Number(params.have) || 0);
    if (!(need > EPS)) return null;
    const deficit = need - have;
    if (!(deficit > EPS)) return null;
    return {
        key: params.key,
        name: params.name,
        article: params.article || undefined,
        dimensionName: params.dimensionName || undefined,
        need,
        have,
        deficit,
        status: have > EPS ? 'insufficient' : 'missing',
        productId: params.productId,
        serviceId: params.serviceId,
        price: params.price,
        priceRate: params.priceRate,
        priceSale: params.priceSale,
        priceSaleRate: params.priceSaleRate,
        asOtherExpense: params.asOtherExpense,
    };
};

/**
 * Current material quantity on the object warehouse by productId.
 * Uses stocks (not operations history) so reversals/write-offs are reflected.
 */
export const buildObjectProductHaveMap = (stocks: unknown): Map<number, number> => {
    const map = new Map<number, number>();
    const list = Array.isArray(stocks) ? stocks : [];
    for (const item of list) {
        const row = asRecord(item);
        if (!row) continue;
        const productId = pickNumber(row, 'productId', 'product_id');
        const count = pickNumber(row, 'stockCount', 'stock_count');
        if (!(productId > 0) || !(count > 0)) continue;
        map.set(productId, (map.get(productId) || 0) + count);
    }
    return map;
};

type ServiceHaveKey = `id:${number}` | `name:${string}`;

interface ServiceHaveEntry {
    key: ServiceHaveKey;
    serviceId: number;
    name: string;
    have: number;
}

/** Aggregate active team services by catalog id or normalized name. */
export const buildObjectServiceHaveMap = (
    expendTeams: unknown,
    otherOnly = false,
): Map<ServiceHaveKey, ServiceHaveEntry> => {
    const map = new Map<ServiceHaveKey, ServiceHaveEntry>();
    const list = Array.isArray(expendTeams) ? expendTeams : [];

    for (const item of list) {
        const row = asRecord(item);
        if (!row) continue;
        const status = pickNumber(row, 'income_status', 'incomeStatus');
        if (status === vconst.incomeStatCancel) continue;
        if (isOtherTeamIncome(row) !== otherOnly) continue;

        const serviceId = pickNumber(row, 'service_id', 'serviceId');
        const name =
            pickText(row, 'income_name', 'incomeName') ||
            (serviceId > 0 ? `#${serviceId}` : '');
        const count = Math.abs(pickNumber(row, 'income_count', 'incomeCount'));
        if (!(count > 0)) continue;

        const key: ServiceHaveKey =
            serviceId > 0 ? `id:${serviceId}` : `name:${normalizeName(name)}`;
        const existing = map.get(key);
        if (existing) {
            existing.have += count;
            if (!existing.name && name) existing.name = name;
            continue;
        }
        map.set(key, { key, serviceId, name, have: count });
    }
    return map;
};

const resolveServicePrices = (item: ProposalExtraItemType) => {
    // Новий формат: price = cost, priceSale = sale. Legacy: лише price (= sale).
    const hasSale = item.priceSale != null || item.priceSaleRate != null;
    const priceSale = hasSale ? Number(item.priceSale || 0) : Number(item.price || 0);
    const priceSaleRate = hasSale
        ? item.priceSaleRate != null
            ? Number(item.priceSaleRate)
            : undefined
        : item.priceRate != null
          ? Number(item.priceRate)
          : undefined;
    const price = hasSale ? Number(item.price || 0) : 0;
    const priceRate = hasSale
        ? item.priceRate != null
            ? Number(item.priceRate)
            : undefined
        : undefined;
    return { price, priceRate, priceSale, priceSaleRate };
};

const buildServiceDemand = (
    items: ProposalExtraItemType[],
    asOtherExpenseOnly: boolean,
) => {
    type Demand = {
        key: ServiceHaveKey;
        serviceId: number;
        name: string;
        need: number;
        price: number;
        priceRate?: number;
        priceSale: number;
        priceSaleRate?: number;
        asOtherExpense?: boolean;
    };
    const map = new Map<ServiceHaveKey, Demand>();

    const list = items || [];
    for (let index = 0; index < list.length; index += 1) {
        const item = list[index];
        const need = Number(item.itemCount || 0);
        if (!(need > 0)) continue;
        const asOtherExpense = isProposalExtraAsOtherExpense(item);
        if (asOtherExpense !== asOtherExpenseOnly) continue;
        const serviceId = Number(
            item.serviceId || (item as { service_id?: number }).service_id || 0,
        );
        const name = String(item.itemName || '').trim() || `#${serviceId || index + 1}`;
        const key: ServiceHaveKey =
            serviceId > 0 ? `id:${serviceId}` : `name:${normalizeName(name)}`;
        const prices = resolveServicePrices(item);
        const existing = map.get(key);
        if (existing) {
            const totalNeed = existing.need + need;
            if (totalNeed > 0) {
                existing.price =
                    (existing.need * existing.price + need * prices.price) / totalNeed;
                existing.priceSale =
                    (existing.need * existing.priceSale + need * prices.priceSale) / totalNeed;
                if (existing.priceRate != null || prices.priceRate != null) {
                    existing.priceRate =
                        ((existing.priceRate ?? existing.price) * existing.need +
                            (prices.priceRate ?? prices.price) * need) /
                        totalNeed;
                }
                if (existing.priceSaleRate != null || prices.priceSaleRate != null) {
                    existing.priceSaleRate =
                        ((existing.priceSaleRate ?? existing.priceSale) * existing.need +
                            (prices.priceSaleRate ?? prices.priceSale) * need) /
                        totalNeed;
                }
            }
            existing.need = totalNeed;
            if (!existing.name && name) existing.name = name;
            continue;
        }
        map.set(key, { key, serviceId, name, need, asOtherExpense, ...prices });
    }
    return Array.from(map.values());
};

export const getProposalProductShortages = async (
    generation: ObjectProposalGenerationType,
    stocks: unknown,
): Promise<ProposalShortageItem[]> => {
    const { demand } = await buildDemand(generation.products || []);
    if (!demand.length) return [];
    const haveMap = buildObjectProductHaveMap(stocks);

    return demand
        .map((item) =>
            toShortageItem({
                key: `product-${item.productId}`,
                name: item.prodName || `#${item.productId}`,
                article: item.prodArticle,
                dimensionName: item.dimensionName,
                need: item.need,
                have: haveMap.get(item.productId) || 0,
                productId: item.productId,
            }),
        )
        .filter(Boolean) as ProposalShortageItem[];
};

export const getProposalServiceShortages = (
    generation: ObjectProposalGenerationType,
    expendTeams: unknown,
    extras?: { bucket?: 'services' | 'other' },
): ProposalShortageItem[] => {
    const otherOnly = extras?.bucket === 'other';
    const additionalItems = generation.additionalItems || [];
    const demand = buildServiceDemand(additionalItems, otherOnly);
    const haveMap = buildObjectServiceHaveMap(expendTeams, otherOnly);
    const keyPrefix = otherOnly ? 'other' : 'service';

    return demand
        .map((item) =>
            toShortageItem({
                key: `${keyPrefix}-${item.key}`,
                name: item.name,
                need: item.need,
                have: haveMap.get(item.key)?.have || 0,
                serviceId: item.serviceId || undefined,
                price: item.price,
                priceRate: item.priceRate,
                priceSale: item.priceSale,
                priceSaleRate: item.priceSaleRate,
                asOtherExpense: otherOnly ? true : undefined,
            }),
        )
        .filter(Boolean) as ProposalShortageItem[];
};

export const buildProposalShortageResult = async (
    generation: ObjectProposalGenerationType,
    objectData: { stocks?: unknown; expendTeams?: unknown; expends?: unknown } | null | undefined,
): Promise<ProposalShortageResult> => {
    const [products, services] = await Promise.all([
        getProposalProductShortages(generation, objectData?.stocks),
        Promise.resolve(
            getProposalServiceShortages(generation, objectData?.expendTeams, {
                bucket: 'services',
            }),
        ),
    ]);

    return {
        generationId: generation.ID,
        proposalName: String(generation.proposalName || '').trim(),
        products,
        services,
    };
};
